import React, { useState, Component } from 'react';
import { MoonStarIcon, SmileIcon, SparklesIcon, WandIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { SummaryPopup } from './SummaryPopup';
import { LoadingBunny } from './LoadingBunny';
import { Customize } from './Customize';
type Mood = 'calm' | 'silly' | 'magical';
type Topic = 'dragons' | 'cupcakes' | 'space' | 'animals';
export const EasyCreate = () => {
  const [name, setName] = useState('');
  const [selectedMood, setSelectedMood] = useState<Mood | null>(null);
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const moods = [{
    id: 'calm',
    label: 'Calm',
    icon: <MoonStarIcon size={24} data-id="element-399" />
  }, {
    id: 'silly',
    label: 'Silly',
    icon: <SmileIcon size={24} data-id="element-400" />
  }, {
    id: 'magical',
    label: 'Magical',
    icon: <WandIcon size={24} data-id="element-401" />
  }];
  const topics: {
    id: Topic;
    label: string;
  }[] = [{
    id: 'dragons',
    label: 'Dragons'
  }, {
    id: 'cupcakes',
    label: 'Cupcakes'
  }, {
    id: 'space',
    label: 'Space'
  }, {
    id: 'animals',
    label: 'Animals'
  }];
  const toggleTopic = (topic: Topic) => {
    setSelectedTopics(prev => prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSummary(true);
  };
  const handleCreateStory = () => {
    setShowSummary(false);
    setIsLoading(true);
    // Simulate loading time for animation (remove this in production)
    setTimeout(() => {
      setIsLoading(false);
      // TODO: Handle actual story creation
      console.log({
        name,
        selectedMood,
        selectedTopics
      });
    }, 3000);
  };
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only take the first word (no spaces allowed)
    const singleWord = e.target.value.trim().split(' ')[0];
    setName(singleWord);
  };
  return <div className="min-h-screen bg-[#FFFBF6] flex flex-col items-center justify-center p-4" data-id="element-402">
      {isLoading && <LoadingBunny data-id="element-403" />}
      {showSummary && <SummaryPopup name={name} topics={selectedTopics.map(topic => topics.find(t => t.id === topic)?.label || topic)} mood={moods.find(m => m.id === selectedMood)?.label.toLowerCase() || ''} onClose={() => setShowSummary(false)} onCreate={handleCreateStory} data-id="element-404" />}
      <div className="w-full max-w-lg text-center" data-id="element-405">
        <h1 className="text-4xl font-bold text-[#6B4B3E] mb-16" data-id="element-406">Story Bunny</h1>
        <h2 className="text-2xl font-bold text-[#6B4B3E] mb-4" data-id="element-407">
          Make a story about {name || '...'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-8" data-id="element-408">
          {/* Name Input */}
          <div data-id="element-409">
            <input type="text" value={name} onChange={handleNameChange} placeholder="Enter name" className="w-1/2 mx-auto px-6 py-3 text-lg text-[#6B4B3E] bg-white border-2 border-[#E5C1A9] rounded-full focus:outline-none focus:border-[#D4A088] placeholder-[#C4A497] block text-center" data-id="element-410" />
          </div>
          {/* Topic Selection */}
          <div className="space-y-2" data-id="element-411">
            <h3 className="text-2xl font-bold text-[#6B4B3E]" data-id="element-412">With</h3>
            <div className="flex flex-wrap justify-center gap-3" data-id="element-413">
              {topics.map(topic => <button key={topic.id} type="button" onClick={() => toggleTopic(topic.id)} className={`px-6 py-2 rounded-full border-2 transition-all ${selectedTopics.includes(topic.id) ? 'bg-[#E5C1A9] border-[#D4A088] text-[#6B4B3E]' : 'bg-white border-[#E5C1A9] text-[#6B4B3E] hover:border-[#D4A088]'}`} data-id="element-414">
                  {topic.label}
                </button>)}
            </div>
          </div>
          {/* Mood Selection */}
          <div className="space-y-2" data-id="element-415">
            <h3 className="text-2xl font-bold text-[#6B4B3E]" data-id="element-416">That's</h3>
            <div className="flex justify-center gap-6" data-id="element-417">
              {moods.map(mood => <button key={mood.id} type="button" onClick={() => setSelectedMood(mood.id as Mood)} className={`flex flex-col items-center space-y-2 p-4 rounded-full transition-all ${selectedMood === mood.id ? 'bg-[#E5C1A9] text-[#6B4B3E] scale-110' : 'bg-white text-[#6B4B3E] hover:bg-[#FFF0E6]'}`} data-id="element-418">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${mood.id === 'calm' ? 'bg-[#E8F0FF]' : mood.id === 'silly' ? 'bg-[#FFF3D4]' : 'bg-[#F9E8FF]'}`} data-id="element-419">
                    {mood.icon}
                  </div>
                  <span className="font-medium" data-id="element-420">{mood.label}</span>
                </button>)}
            </div>
          </div>
        </form>
        {/* Add More Ideas Button */}
        <div className="mt-8" data-id="element-421">
          <button onClick={() => setShowCustomize(!showCustomize)} className="flex items-center justify-center space-x-2 px-6 py-3 text-[#6B4B3E] border-2 border-[#E5C1A9] rounded-full hover:bg-[#FFF0E6] transition-all duration-200 mx-auto" data-id="element-422">
            <span data-id="element-423">Add more ideas (optional)</span>
            {showCustomize ? <ChevronUpIcon size={20} data-id="element-424" /> : <ChevronDownIcon size={20} data-id="element-425" />}
          </button>
        </div>
        {/* Customize Component */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${showCustomize ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-426">
          <Customize name={name} topics={selectedTopics.map(topic => topics.find(t => t.id === topic)?.label || topic)} mood={moods.find(m => m.id === selectedMood)?.label.toLowerCase() || ''} data-id="element-427" />
        </div>
        {/* Submit Button - Moved to bottom */}
        <button onClick={handleSubmit} disabled={!name || !selectedMood || selectedTopics.length === 0} className="px-8 py-4 bg-[#D4A088] text-white rounded-full text-lg font-medium hover:bg-[#C28B73] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-8 flex items-center justify-center mx-auto" data-id="element-428">
          <SparklesIcon className="mr-2" size={24} data-id="element-429" />
          Create The Story
        </button>
      </div>
      {/* Decorative Bunnies */}
      <div className="fixed bottom-0 left-0 w-24 h-24 p-4" data-id="element-430">
        <div className="text-[#E5C1A9] text-6xl transform -rotate-12" data-id="element-431">🐰</div>
      </div>
      <div className="fixed bottom-0 right-0 w-24 h-24 p-4" data-id="element-432">
        <div className="text-[#E5C1A9] text-6xl transform rotate-12" data-id="element-433">🐰</div>
      </div>
    </div>;
};